import React from "react"
import { Col, Container, Row } from "reactstrap"
import styled from "styled-components"
import PageTemplate from "templates/PageTemplate"
import useLocoScroll from "hooks/useLocoScroll"
import useAboutUs from "hooks/useAboutUs"
import useImagePreloader from "hooks/useImagePreloader"
import ImgAbout1 from "assets/images/about1.png"
import ImgAbout2 from "assets/images/about2.png"
import ImgAbout3 from "assets/images/about3.png"
import ImgAbout4 from "assets/images/about4.png"
import ImgAbout5 from "assets/images/about5.png"
import ImgAbout6 from "assets/images/about6.png"
import Seo from "components/common/Seo"
import WhyChooseUs from "components/core/WhyChooseUs"
import BackToTop from "components/common/BackToTop"
import vars from "styles/variables"

const preloadSrcList = [
  ImgAbout1,
  ImgAbout2,
  ImgAbout3,
  ImgAbout4,
  ImgAbout5,
  ImgAbout6,
]

function AboutUsPage() {
  const { imagesPreloaded } = useImagePreloader(preloadSrcList)
  const { aboutUsRef } = useAboutUs(imagesPreloaded)

  const { locoScrollTop } = useLocoScroll(imagesPreloaded)

  if (!imagesPreloaded) return null

  return (
    <StyledAboutUsPage ref={aboutUsRef}>
      <Seo title="About Us" />
      <BackToTop onClick={locoScrollTop} />
      <PageTemplate title="About us" hideTitle style={{ paddingBottom: 70 }}>
        <Container>
          <Row className="mb-5" data-scroll-section>
            <Col xs="12" lg="6">
              <StyledAboutUs className="about-us-title">
                <div
                  data-scroll
                  data-scroll-speed="2"
                  data-scroll-position="top"
                  data-scroll-direction="horizontal"
                  style={{ marginBottom: 2 }}
                >
                  <span>a</span>
                </div>
                <div
                  data-scroll
                  data-scroll-speed="1"
                  data-scroll-position="top"
                  data-scroll-direction="horizontal"
                  style={{ marginBottom: -6, marginLeft: -1 }}
                >
                  <span>b</span>
                </div>
                <div
                  data-scroll
                  data-scroll-speed="0.5"
                  data-scroll-position="top"
                  data-scroll-direction="horizontal"
                >
                  <span>out us</span>
                </div>
              </StyledAboutUs>
            </Col>
          </Row>

          <Row className="mb-5 align-items-start" data-scroll-section>
            <Col xs="12" lg="6">
              <p>
                At I/O Atelier, we’ve been on a journey to redefine the future
                of design since our inception. From a bold idea to a thriving
                team of architects, creatives, engineers, and technologists,
                we’ve proven that creativity and data can work in harmony to
                shape the built environment.
                <br />
                <br />
                Our approach seamlessly integrates technology and artistry,
                blending data-driven insights with design ingenuity to create
                spaces that inspire and endure. We are united by our mission to
                design for people, crafting human-centric, intuitive and
                meaningful environments.
              </p>
            </Col>

            <Col xs="12" lg="6">
              <img
                src={ImgAbout1}
                alt="Net Zero Graph"
                className="img-fluid w-100"
                style={{ borderRadius: "20px" }}
              />
            </Col>
          </Row>

          <Row className="mb-2" data-scroll-section>
            <Col xs="12" lg="12" style={{ display: "flex" }}>
              <h2 style={{ fontWeight: "bold", textAlign: "right" }}>
                Our Mission
              </h2>
            </Col>
          </Row>

          <Row className="mb-5 align-items-start" data-scroll-section>
            <Col xs="12" lg="6">
              <img
                src={ImgAbout2}
                alt="Net Zero Graph"
                className="img-fluid w-100 mb-4"
                style={{ borderRadius: "20px" }}
              />
              <p
                style={{
                  fontSize: "0.7rem",
                  fontWeight: "300",
                  marginTop: "10px",
                  textAlign: "left",
                  paddingRight: "5%",
                }}
              >
                Private Members Club in Dubai
              </p>
            </Col>

            <Col xs="12" lg="6">
              <p>
                Enhance the human experience through thoughtful design.
                <br />
                <br />
                Eliminate wasteful processes and prioritize sustainability.
                <br />
                <br />
                Deliver innovative, future-proof solutions that leave a lasting
                impact.
                <br />
                <br />
                Continuously explore new sustainable materials and techniques to
                drive innovation.
              </p>
            </Col>
          </Row>

          <Row className="w-100 d-flex justify-content-end" data-scroll-section>
            <Col xs="12" lg="12">
              <div
                className="mb-5"
                style={{
                  width: "100%",
                  textAlign: "right",
                  textAlignLast: "right",
                  direction: "ltr",
                  paddingRight: "5%",
                }}
              >
                <h2 style={{ fontWeight: "bold", textAlign: "right" }}>
                  What sets us apart{" "}
                </h2>
                <p
                  style={{
                    fontSize: "2rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  We believe that great design starts with great data, and our
                  commitment to excellence drives everything we do.
                </p>
              </div>
            </Col>
          </Row>

          <Row className="mb-5 align-items-center" data-scroll-section>
            <Col xs="12" lg="12">
              <GridContainer>
                <LeftImage>
                  <Image src={ImgAbout3} alt="Top aligned" />
                  <Caption align="left">
                    DIFC Office Tower Lobby in Dubai
                  </Caption>
                </LeftImage>

                <TextBlock>
                  <h3>Multidisciplinary Expertise</h3>
                  <p>
                    We bring together architects, creatives, ML engineers, data
                    scientists, and UX/UI product engineers to form a truly
                    integrated team.
                    <br />
                    <br />
                    Our unique blend of skills enables us to push boundaries and
                    redefine what's possible in design.
                  </p>

                  <h3>AI-Augmented Workflows</h3>
                  <p>
                    By integrating AI into our processes, we eliminate
                    inefficiencies, allowing us to focus more resources on
                    creativity and innovation.
                    <br />
                    <br />
                    Our custom workflows enable faster project delivery without
                    compromising quality.
                  </p>
                </TextBlock>

                <TextBlock>
                  <h3>Client-Centric Solutions</h3>
                  <p>
                    Every project is tailored to meet our clients' unique needs,
                    supported by actionable insights and collaborative
                    engagement.
                  </p>

                  <h3>Sustainability Commitment</h3>
                  <p>
                    We design with purpose, prioritizing sustainable practices
                    and minimizing environmental impact.
                  </p>
                </TextBlock>

                <RightImage>
                  <Image src={ImgAbout4} alt="Bottom aligned" />
                  <Caption align="right">BNP Paribs Office Space</Caption>
                </RightImage>
              </GridContainer>
            </Col>
          </Row>

          <Row className="w-100 d-flex justify-content-end" data-scroll-section>
            <Col xs="12" lg="12">
              <div
                className="mb-5"
                style={{
                  width: "100%",
                  textAlign: "left",
                  textAlignLast: "left",
                  direction: "ltr",
                  paddingRight: "5%",
                }}
              >
                <h2 style={{ fontWeight: "bold", textAlign: "right" }}>
                  Our Process
                </h2>
                <p
                  style={{
                    fontSize: "2rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  At the heart of I/O Atelier is a process that combines
                  creativity, collaboration, and cutting-edge technology.
                </p>
                <p
                  style={{
                    fontSize: "1rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  Our process is built to amplify creative thinking and deliver
                  designs that stand the test. We bring your vision to life by ,
                </p>
                <p
                  style={{
                    fontSize: "1rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>Engaging</strong> and understanding your needs and
                  aspirations,
                </p>
                <p
                  style={{
                    fontSize: "1rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>Analysing</strong> and using data insights to inform
                  every step of the design process,
                </p>
                <p
                  style={{
                    fontSize: "1rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>Conceptualizing</strong> and developing creative
                  solutions tailored to your vision,
                </p>
                <p
                  style={{
                    fontSize: "1rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>Developing</strong> and employing AI-driven workflows
                  to enhance productivity and precision,
                </p>
                <p
                  style={{
                    fontSize: "1rem",
                    fontWeight: "400",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>Delivering</strong> exceptional results, seamlessly
                  and sustainably.
                </p>
              </div>
            </Col>
          </Row>

          <Row className="mb-5" data-scroll-section>
            <Col xs="12" lg="12">
              <div
                style={{
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <img
                  src={ImgAbout5}
                  alt="Al Khan Sharif Project"
                  style={{
                    width: "100%",
                    maxWidth: "100vw",
                    borderRadius: "20px",
                    display: "block",
                    margin: "0 auto",
                  }}
                />

                <p
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "300",
                    marginTop: "10px",
                    textAlign: "right",
                    paddingRight: "0%",
                  }}
                >
                  Private residence in Dubai
                </p>
              </div>
            </Col>
          </Row>

          <Row className="mb-5 align-items-start" data-scroll-section>
            <Col xs="12" lg="6">
              <img
                src={ImgAbout6}
                alt="Net Zero Graph"
                className="img-fluid w-100 mb-4 mb-md-0"
                style={{
                  borderRadius: "20px",
                }}
              />
            </Col>

            <Col xs="12" lg="6">
              <h2>Sustainability: Building for the Future</h2>
              <p>
                We take a pragmatic and actionable approach to sustainability by
                embedding it into every project. We ensure our designs
                contribute to a better future through:
              </p>
              <p>
                <strong>Lifecycle Design: </strong>Every material and object we
                specify is considered for its entire lifecycle, ensuring it can
                be re-used, re-purposed, or recycled wherever possible.
                <br />
                <br />
                <strong>Minimizing Virgin Materials: </strong>We prioritize the
                use of recycled and sustainable materials, avoiding virgin
                resources whenever feasible.
                <br />
                <br />
                <strong>Reducing Embodied Carbon: </strong>Collaborating with
                the wider project team, we focus on reducing embodied carbon
                throughout the design process.
                <br />
                <br />
                <strong>Actionable Reporting: </strong>By the end of RIBA Stage
                2, we deliver a detailed report outlining key decisions clients
                can take to achieve low, neutral, or negative embodied carbon
                outcomes.
                <br />
                <br />
                <strong>Empowering Clients: </strong>We provide the data,
                insights, and solutions to guide our clients in making informed
                decisions while supporting them throughout the sustainability
                journey.
              </p>
            </Col>
          </Row>

          <Row className="w-100 d-flex justify-content-end" data-scroll-section>
            <Col xs="12" lg="12">
              <div
                className="mb-5"
                style={{
                  width: "100%",
                  textAlign: "right",
                  textAlignLast: "right",
                  direction: "ltr",
                  paddingRight: "5%",
                }}
              >
                <p
                  style={{
                    fontSize: "2rem",
                    fontWeight: "200",
                    lineHeight: "1.6",
                  }}
                >
                  Sustainability is not just a goal; it's a shared commitment to
                  creating designs that are as impactful for people as they are
                  for the planet.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
        <WhyChooseUs />
      </PageTemplate>
    </StyledAboutUsPage>
  )
}

export default AboutUsPage

const StyledAboutUsPage = styled.div`
  .about-us-content {
    p {
      opacity: 0;
      transform: translate(60px, 60px);
    }
  }

  .about-images,
  .about-first-quote,
  .about-circle-image {
    opacity: 0;
    transform: translate(0, 80px);
  }
`

const StyledAboutUs = styled.h2`
  margin: 0;
  font-weight: bold;
  line-height: 1;
  font-size: 56px;
  letter-spacing: 7px;

  span {
    opacity: 0;
    display: inline-block;
    transform: rotate(-45deg);
    transform: translate(40px, 40px);
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr;
  grid-template-rows: auto auto auto auto auto;
  padding: 20px;

  @media (max-width: ${vars.screen_md_max}) {
    display: flex;
    flex-direction: column;
    padding: 0;
  }
`

const LeftImage = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 6;
  display: flex;
  flex-direction: column;

  @media (max-width: ${vars.screen_md_max}) {
    grid-column: auto;
    grid-row: auto;
  }
`

const RightImage = styled.div`
  grid-column: 3 / 4;
  grid-row: 4 / 6;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  @media (max-width: ${vars.screen_md_max}) {
    align-items: flex-start;
    text-align: left;
    grid-column: auto;
    grid-row: auto;
  }
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: left;
  padding: 20px;

  @media (max-width: ${vars.screen_md_max}) {
    grid-column: auto;
    grid-row: auto;
    padding-left: 0;
    padding-right: 0;
  }

  &:nth-child(2) {
    grid-column: 2 / 4;
    grid-row: 1 / 4;
    padding-bottom: 0;

    @media (max-width: ${vars.screen_md_max}) {
      grid-column: auto;
      grid-row: auto;
    }
  }

  &:nth-child(3) {
    padding-top: 0;

    @media (max-width: ${vars.screen_md_max}) {
      grid-column: auto;
      grid-row: auto;
    }
  }
`

const Caption = styled.p`
  font-size: 0.7rem;
  font-weight: 300;
  margin-top: 10px;
  text-align: ${({ align }) => align || "left"};
  padding-right: ${({ align }) => (align === "left" ? "5%" : "0")};
  width: 100%;
`

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
`
